<template>
    <side-panel>
        <template #head>
            <div class="a-header">
                <div class="d-flex align-items-center justify-content-between">
                    <h2 class="pt-3">{{ __('manage_poi.title') }}</h2>
                    <golia-button variant="btn-sm btn-fill dark-blue mt-2 ms-2" icon="fa-fw fa-gear"
                        @click="showSettings = true" v-b-modal.poi-manager-settings-modal />
                </div>
                <!-- Settings modal -->
                <b-modal id="poi-manager-settings-modal" :title="__('manage_poi.notifications.settings_title')"
                    size="lg" body-class="custom-scrollbar" scrollable>
                    <poi-settings v-model="poiSettings" />
                    <template #modal-footer="{ cancel }">
                        <golia-button @click="cancel()" :label="__('cancel')" variant="btn-fill gray" />
                        <golia-button @click="saveAlertSettings()" :label="__('save')" variant="btn-fill success" />
                    </template>
                </b-modal>
                <div class="close-btn" @click="$emit('toggleUtilityPanel')">
                    <i class="fa-solid fa-xmark"></i>
                </div>
            </div>
        </template>

        <template #body>
            <div v-resized="resized" class="d-flex flex-row h-100">
                <div class="map-section">
                    <b-alert class="select-on-map" variant="info" dismissible fade show
                        v-if="singlePoi.address && (!singlePoi.lat || !singlePoi.lng)">
                        {{ __('planner.select_on_map') }}
                    </b-alert>
                    <geofleet-map class="geofleet-map smaller-controls" map="silica" :advanced="true" :cluster="true"
                        :ptvToken="$env.ptv_password" :extraControls="{}" @readyMap="clickOnMapHandler" ref="map"
                        :hideTrucks="true">
                        <!-- POI Geofencing area -->
                        <template v-if="mode == 'list'">
                            <l-circle v-for="poi in localPoisCircles" :key="'circle-' + poi.id" :lat-lng="poi.latLng"
                                :radius="poi.radius" :color="poiColor(poi)" @click="handleFocusPoi(poi)">
                            </l-circle>
                            <l-polygon v-for="poi in localPoisPolygons" :key="'poly-' + poi.id"
                                :lat-lngs="polygonLatLngsFromGeoJson(poi.polygon_geojson)"
                                @click="handleFocusPoi(poi)" />
                        </template>
                        <!-- POI list markers -->
                        <template v-if="mode == 'list'">
                            <l-marker-cluster :options="poiClusterOptions">
                                <custom-marker v-for="poi in localPoisMarkers" :key="'poi-' + poi.id"
                                    :item="poi" :coordinates="poi.displayLatLng" :markerTitle="poi.description"
                                    :iconUnicode="poi.type_icon ? poiIconUnicode(poi) : '#xf3c5'"
                                    @click="handleFocusPoi(poi)" :customizer="{
                                        tiny: true,
                                        color_class: poiClass(poi),
                                        custom_color: poiColor(poi)
                                    }" />
                            </l-marker-cluster>
                        </template>
                        <!-- Goefencing and markers of selected POI (or new one) -->
                        <template v-if="mode != 'list' && singlePoi.lat && singlePoi.lng">
                            <l-circle v-if="singlePoi.area_type !== 'polygon' && singlePoi.radius"
                                :lat-lng="[singlePoi.lat, singlePoi.lng]" :radius="singlePoi.radius"
                                :color="poiColor(singlePoi)">
                            </l-circle>
                            <custom-marker :key="mode + '-poi-' + singlePoi.id" :item="singlePoi"
                                :coordinates="[singlePoi.lat, singlePoi.lng]" :markerTitle="singlePoi.description"
                                :iconUnicode="singlePoi.type_icon ? poiIconUnicode(singlePoi) : '#xf3c5'"
                                :draggable="true" :customizer="{
                                    tiny: true,
                                    color_class: 'poi-manager-marker',
                                    custom_color: poiColor(singlePoi),
                                    size_x: !singlePoi.type_icon ? 38 : null
                                }" @updateByDrag="movePoiCoordinates" />
                        </template>
                    </geofleet-map>
                </div>
                <div class="pois-list-wrapper">
                    <!-- Search and filters -->
                    <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
                        <golia-input v-if="mode == 'list'" v-model="searchPoi" style="min-width: 20px"
                            :placeholder="__('cerca')">
                        </golia-input>

                        <golia-input v-if="mode == 'list'" id="type" type="select" :options="poiTypes"
                            v-model="poiTypesSelected" style="min-width: 20px; max-width: 100px">
                        </golia-input>

                        <golia-button variant="btn-success btn-add-poi" icon="fa-fw fa-plus"
                            :label="__('manage_poi.add_poi')" @click="mode = 'create'" v-show="mode == 'list'" />
                        <golia-button variant="btn-fill dark-blue" icon="fa-fw fa-arrow-left" style="margin-right: 5px"
                            :label="__('go_back')" @click="mode = 'list'" v-show="mode != 'list'" />
                    </div>

                    <!-- Lista POI -->
                    <template v-if="mode == 'list'">
                        <dynamic-scroller v-if="localPoisComputed.length > 0" ref="poiScroller"
                            class="pois-list custom-scrollbar" :items="localPoisComputed" :min-item-size="112"
                            key-field="id">
                            <template #default="{ item: poi, index, active }">
                                <dynamic-scroller-item :item="poi" :active="active" :data-index="index"
                                    :size-dependencies="[poi.description, poi.address, poi.on_focus]">
                                    <div class="poi-list-item" :id="poi.id"
                                        :class="{ 'focused-poi': poi.on_focus }" @click="handleFocusPoi(poi)">
                                        <golia-list-item :style="{ '--item-border-color': poiColor(poi) }"
                                            class="align-items-center">
                                            <template #extra-contents>
                                                <h3 class="item-title d-flex align-items-center gap-2">
                                                    {{ poi.description }}
                                                    <span class="fa-stack fa-xs" v-if="poisNotifications[poi.id]">
                                                        <i
                                                            class="fa-solid fa-circle fa-stack-2x text-success"></i>
                                                        <i
                                                            class="fa-fw fa-solid fa-bell fa-stack-1x text-white"></i>
                                                    </span>
                                                </h3>
                                                <p class="item-subtitle">
                                                    <span class="icon-wrapper">
                                                        <i class="poi-icon" v-html="poi.type_icon"></i>
                                                    </span>
                                                    <span>{{ getTranslatedDescription(poi) }}</span>
                                                </p>
                                                <p class="item-subtitle item-address">
                                                    <span class="icon-wrapper">
                                                        <i class="fa-fw fa-solid fa-location-dot"></i>
                                                    </span>
                                                    <poi-address class="address-text" :poi="poi" />
                                                </p>
                                            </template>
                                            <template #actions>
                                                <div class="d-flex flex-column gap-2">
                                                    <golia-button variant="btn-info btn-sm" icon="fa-fw fa-pencil"
                                                        @click.stop="showUpdatePoi(poi)" />
                                                    <golia-button variant="btn-danger btn-sm" icon="fa-fw fa-trash"
                                                        @click.stop="handleDeletePoi(poi)" />
                                                </div>
                                            </template>
                                        </golia-list-item>
                                    </div>
                                </dynamic-scroller-item>
                            </template>
                        </dynamic-scroller>
                        <div v-else>
                            <div class="no-pois d-flex align-items-center justify-content-center h-100 bg-main-gray">
                                <div class="text-center mt-3">
                                    <i class="fa-solid fa-info-circle fa-2x text-muted text-dark-blue"></i>
                                    <p class="mt-3 text-muted">{{ __('manage_poi.no_pois') }}</p>
                                </div>
                            </div>
                        </div>
                    </template>
                    <!-- Creazione / Modifica POI -->
                    <template v-else>
                        <div class="form-container">
                            <poi-form v-model="singlePoi" @store="handleCreatePoi" @update="handleUpdatePoi"
                                @searchAddress="centerMap" @handlePoiNotification="sendAlertConfig"
                                @removePoiNotification="deleteAlertConfig" />
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </side-panel>
</template>

<script>
    import SidePanel from './SidePanel.vue'
    import PoiForm from './PoiManagerPanel/PoiForm.vue'
    import PoiSettings from './PoiManagerPanel/PoiSettings.vue'
    import PoiAddress from './PoiManagerPanel/PoiAddress.vue'
    import { LCircle, LPolygon } from 'vue2-leaflet'
    import L from 'leaflet'
    import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'
    import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller'
    import 'leaflet-draw'
    import 'leaflet-path-drag'
    import 'leaflet-draw/dist/leaflet.draw.css'

    import { mapMutations, mapActions, mapState } from 'vuex'

    export default {
        name: 'PoiManagerPanel',
        components: {
            SidePanel,
            LCircle,
            LPolygon,
            PoiForm,
            PoiSettings,
            PoiAddress,
            DynamicScroller,
            DynamicScrollerItem,
            'l-marker-cluster': Vue2LeafletMarkerCluster
        },
        data() {
            return {
                poiAlertTypes: [
                    { value: 'entry', text: this.__('manage_poi.alert_types.in') },
                    { value: 'exit', text: this.__('manage_poi.alert_types.out') },
                    { value: 'both', text: this.__('manage_poi.alert_types.both') }
                ],
                poiTypesSelected: '',
                searchPoi: '',
                mode: 'list',
                mapZoom: 0,
                mapBounds: null,
                renderedPoiMarkerCount: 0,
                singlePoi: {
                    id: null,
                    description: null,
                    address: null,
                    address_from_source: null,
                    lat: null,
                    lng: null,
                    radius: null,
                    area_type: 'circle',
                    polygon_geojson: null,
                    type: null,
                    note: null,
                    color: null
                },
                poiClusterOptions: {
                    maxClusterRadius: 30,
                    showCoverageOnHover: true,
                    zoomToBoundsOnClick: true,
                    removeOutsideVisibleBounds: true,
                    elementsPlacementStrategy: 'clock',
                    helpingCircles: true,
                    spiderfyDistanceSurplus: 25,
                    spiderfyDistanceMultiplier: 1,
                    elementsMultiplier: 1.4,
                    firstCircleElements: 8,
                    spiderfyOnMaxZoom: true,
                    animateAddingMarkers: false,
                    spiderfiedClassName: 'spiderfied-marker',
                    disableClusteringAtZoom: 18,
                    chunkedLoading: true
                },
                showSettings: false,
                pendingPoiCenter: null,
                poiSettings: {
                    emailAddresses: []
                },
                drawLayerGroup: null,
                drawControl: null,
                polygonStyle: {
                    color: '#ffcc00',
                    weight: 1,
                    opacity: 1,
                    fillColor: '#f00000',
                    fillOpacity: 0.12
                }
            }
        },
        created() {
            if (this.storedPoiSettings) {
                this.poiSettings = this.storedPoiSettings
            }
            this.loadPoiCategories({ vueInstance: this })
            this.loadPoisNotifications({ vueInstance: this })

            this.seedNewPoiFromRoute()
        },
        methods: {
            handleCreatePoi(poi) {
                this.createPoi({ vueInstance: this, item: poi })
                this.mode = 'list'
            },
            handleUpdatePoi(poi) {
                this.updatePoiOnServer({ vueInstance: this, item: poi })
                this.mode = 'list'
            },
            handleDeletePoi(poi) {
                this.$bvModal
                    .msgBoxConfirm(this.__('manage_poi.messages.delete_poi_confirm'), {
                        okVariant: 'success',
                        okTitle: this.__('yes'),
                        cancelTitle: this.__('no')
                    })
                    .then((response) => {
                        if (response) {
                            this.deletePoi({ vueInstance: this, item: poi })
                        }
                    })
            },
            resized() {
                const map = this.getPoiLeafletMap()
                if (map) map.invalidateSize({ pan: false })
            },
            getPoiLeafletMap() {
                return (
                    this.$refs?.map?.lmap ||
                    this.$refs?.map?.$refs?.map?.mapObject ||
                    null
                )
            },
            poiColor(poi) {
                return poi.color || poi.type_color || '#000'
            },
            poiClass(poi) {
                return 'poi-manager-marker ' + poi?.type_description?.toLowerCase()?.replace(' ', '-')
            },
            poiIconUnicode(poi) {
                return String(poi.type_icon || '')
                    .replace('&', '')
                    .replace(';', '')
            },
            getTranslatedDescription(poi) {
                const poiToFind = this.poiTypes.find((poi) => poi.value == poi.type)
                const poiLabel = poiToFind ? poiToFind.text : poi.type_description
                return poiLabel || ''
            },
            showUpdatePoi(poi) {
                this.loadPoiCategories({ vueInstance: this })
                this.mode = 'update'
                const notification = this.poisNotifications[poi.id]
                this.singlePoi = {
                    ...poi,
                    area_type: poi.area_type || (poi.polygon_geojson ? 'polygon' : 'circle'),
                    polygon_geojson: poi.polygon_geojson || null,
                    alert: notification
                        ? {
                              ...notification,
                              notification_events: notification.notification_events || []
                          }
                        : { notification_events: [] }
                }
                this.$nextTick(() => this.syncPolygonDrawingMode())
            },
            resetSinglePoi() {
                this.singlePoi = {
                    id: null,
                    description: null,
                    address: null,
                    lat: null,
                    lng: null,
                    radius: null,
                    area_type: 'circle',
                    polygon_geojson: null,
                    type: this.poiCategories[0]?.id || null,
                    note: null,
                    color: null
                }
                this.clearDrawnPolygons()
            },
            centerMap(latLng) {
                const map = this.getPoiLeafletMap()
                if (map) map.setView(latLng, 16)
            },
            fitPoisBounds() {
                const map = this.getPoiLeafletMap()
                if (!map || this.pois.length === 0) return false

                const mapSize = map.getSize()
                if (mapSize.x < 100 || mapSize.y < 100) return false

                const bounds = L.latLngBounds([])

                this.pois.forEach((poi) => {
                    if (poi.displayLatLng) bounds.extend(poi.displayLatLng)
                })

                if (bounds.isValid()) {
                    map.fitBounds(bounds, { padding: [20, 20] })
                    return true
                }

                return false
            },
            clickOnMapHandler() {
                const map = this.$refs.map.$refs.map.mapObject
                this._poiMapReady = true
                this.updatePoiMapViewport()
                this.schedulePoiMarkerRendering()
                this.schedulePoiMapLayout(map)
                this.scheduleInitialPoiBoundsFit()
                map.on('zoomend moveend', this.updatePoiMapViewport)
                map.on('click', (e) => {
                    var coord = e.latlng
                    var lat = coord.lat
                    var lng = coord.lng
                    if (this.mode != 'list' && this.singlePoi.area_type !== 'polygon') {
                        this.singlePoi.lat = lat
                        this.singlePoi.lng = lng
                    }
                })

                this.initLeafletDraw(map)

                // Center on a location seeded via the map context menu ("create new
                // POI here") once the inner map is ready.
                if (this.pendingPoiCenter) {
                    map.setView(this.pendingPoiCenter, 16)
                    this.pendingPoiCenter = null
                }
            },
            schedulePoiMapLayout(map = this.getPoiLeafletMap()) {
                if (!map) return

                const currentTimers = this._poiMapLayoutTimers || []
                currentTimers.forEach((timer) => clearTimeout(timer))
                this._poiMapLayoutTimers = [0, 100, 450].map((delay) =>
                    setTimeout(() => {
                        if (this._isDestroyed) return

                        map.invalidateSize({ pan: false })
                    }, delay)
                )
            },
            scheduleInitialPoiBoundsFit() {
                if (this._poiBoundsFitted || !this._poiMapReady || this.mode !== 'list') return

                clearTimeout(this._poiBoundsFitTimer)
                // The routed panel and the MainPanel width both transition for 400 ms.
                // Fit only after the final invalidateSize has settled.
                this._poiBoundsFitTimer = setTimeout(() => {
                    if (this._isDestroyed || this._poiBoundsFitted) return

                    this._poiBoundsFitted = this.fitPoisBounds()
                    this._poiBoundsFitTimer = null
                    if (!this._poiBoundsFitted && this.pois.length > 0) {
                        this.scheduleInitialPoiBoundsFit()
                    }
                }, 550)
            },
            updatePoiMapViewport() {
                const map = this.getPoiLeafletMap()
                if (!map) return

                const bounds = map.getBounds().pad(0.15)
                this.mapZoom = map.getZoom()
                this.mapBounds = {
                    north: bounds.getNorth(),
                    south: bounds.getSouth(),
                    east: bounds.getEast(),
                    west: bounds.getWest()
                }
            },
            isPoiInsideMapBounds(poi) {
                if (!this.mapBounds || !Array.isArray(poi.displayLatLng)) return false

                const lat = Number(poi.displayLatLng[0])
                const lng = Number(poi.displayLatLng[1])

                return (
                    lat <= this.mapBounds.north &&
                    lat >= this.mapBounds.south &&
                    lng <= this.mapBounds.east &&
                    lng >= this.mapBounds.west
                )
            },
            schedulePoiMarkerRendering() {
                clearTimeout(this._poiMarkerBatchTimer)
                if (this.mode !== 'list' || !this._poiMapReady || !this.getPoiLeafletMap()) {
                    return
                }

                const renderBatch = () => {
                    if (this._isDestroyed || this.mode !== 'list') return

                    const total = this.eligiblePoiMarkers.length
                    if (this.renderedPoiMarkerCount >= total) return

                    this.renderedPoiMarkerCount = Math.min(
                        this.renderedPoiMarkerCount + 50,
                        total
                    )

                    if (this.renderedPoiMarkerCount < total) {
                        this._poiMarkerBatchTimer = setTimeout(renderBatch, 32)
                    }
                }

                renderBatch()
            },
            // Opens the "create POI" form pre-filled with the coordinates passed
            // in the route query by the map context menu ("create new POI here").
            seedNewPoiFromRoute() {
                const lat = parseFloat(this.$route?.query?.new_poi_lat)
                const lng = parseFloat(this.$route?.query?.new_poi_lng)

                if (isNaN(lat) || isNaN(lng)) return

                // Drop the query so a refresh or later navigation doesn't re-seed.
                this.$router.replace({ name: this.$route.name, query: {} }).catch(() => {})

                this.mode = 'create'

                // Apply the seed AFTER the `mode` watcher's resetSinglePoi() has
                // cleared the form — otherwise it would wipe what we set here.
                this.$nextTick(async () => {
                    this.singlePoi.lat = lat
                    this.singlePoi.lng = lng
                    this.pendingPoiCenter = [lat, lng]
                    this.centerMap([lat, lng])

                    // Reverse-geocode the point and fill the address field in the
                    // shape golia-waypoint-search expects ({ label, referenceCoordinate }),
                    // so the locality shows and the form keeps the coordinates.
                    const label = await this.$reverseGeocoding({ lat, lng, full: true }).catch(
                        () => null
                    )
                    if (label) {
                        this.singlePoi.address = {
                            label,
                            referenceCoordinate: { x: lng, y: lat }
                        }
                    }
                })
            },
            initLeafletDraw(map) {
                if (this.drawLayerGroup) return
                this.drawLayerGroup = new L.FeatureGroup()
                map.addLayer(this.drawLayerGroup)
                this.syncPolygonDrawingMode()
                map.on(L.Draw.Event.CREATED, (event) => {
                    if (this.mode === 'list' || this.singlePoi.area_type !== 'polygon') return
                    this.clearDrawnPolygons()
                    const layer = event.layer
                    this.drawLayerGroup.addLayer(layer)
                    this.attachPolygonDragHandlers(layer)
                    this.updateSinglePoiPolygonFromLayer(layer)
                })
                map.on(L.Draw.Event.EDITED, (event) => {
                    event.layers.eachLayer((layer) => {
                        this.updateSinglePoiPolygonFromLayer(layer)
                    })
                })
                map.on(L.Draw.Event.DELETED, () => {
                    this.singlePoi.polygon_geojson = null
                })
            },
            syncPolygonDrawingMode() {
                const map = this.$refs?.map?.$refs?.map?.mapObject
                if (!map || !this.drawLayerGroup) return
                if (this.drawControl) {
                    map.removeControl(this.drawControl)
                    this.drawControl = null
                }
                if (this.mode === 'list' || this.singlePoi.area_type !== 'polygon') {
                    this.clearDrawnPolygons()
                    return
                }
                this.drawControl = new L.Control.Draw({
                    edit: {
                        featureGroup: this.drawLayerGroup,
                        remove: true
                    },
                    draw: {
                        polygon: {
                            shapeOptions: this.getPolygonStyle(this.singlePoi)
                        },
                        rectangle: false,
                        polyline: false,
                        marker: false,
                        circle: false,
                        circlemarker: false
                    }
                })
                map.addControl(this.drawControl)
                this.syncLayerFromSinglePoiPolygon()
            },
            syncLayerFromSinglePoiPolygon() {
                if (!this.drawLayerGroup) return
                this.clearDrawnPolygons()
                if (!this.singlePoi.polygon_geojson) return
                const layer = L.geoJSON(this.singlePoi.polygon_geojson)
                layer.eachLayer((l) => {
                    this.drawLayerGroup.addLayer(l)
                    if (typeof l.setStyle === 'function') l.setStyle(this.getPolygonStyle(this.singlePoi))
                    this.attachPolygonDragHandlers(l)
                })
            },
            clearDrawnPolygons() {
                if (this.drawLayerGroup) this.drawLayerGroup.clearLayers()
            },
            updateSinglePoiPolygonFromLayer(layer) {
                const geojson = layer.toGeoJSON()
                this.singlePoi.polygon_geojson = geojson
                this.singlePoi.area_type = 'polygon'
                if (typeof layer.setStyle === 'function') {
                    layer.setStyle(this.getPolygonStyle(this.singlePoi))
                }
                // Keep lat/lng aligned to polygon center for compatibility with existing map logic.
                const center = layer.getBounds().getCenter()
                this.singlePoi.lat = center.lat
                this.singlePoi.lng = center.lng
            },
            getPolygonStyle(poi) {
                const color = poi?.color || poi?.type_color || this.polygonStyle.color
                return {
                    ...this.polygonStyle,
                    color,
                    fillColor: color
                }
            },
            attachPolygonDragHandlers(layer) {
                if (!layer || typeof layer.on !== 'function') return

                if (typeof layer.setStyle === 'function') {
                    layer.setStyle(this.getPolygonStyle(this.singlePoi))
                }

                // Path-drag expects draggable=true; layers from leaflet-draw are not draggable by default.
                layer.options = layer.options || {}
                layer.options.draggable = true

                // Ensure drag handler exists even if the layer was created before options.draggable was set.
                if (!layer.dragging && L.Handler && L.Handler.PathDrag) {
                    layer.dragging = new L.Handler.PathDrag(layer)
                }

                if (layer.dragging && typeof layer.dragging.enable === 'function') {
                    layer.dragging.enable()
                }

                // Avoid duplicate binding when syncing from geojson multiple times
                if (layer._poiPolygonDragBound) return

                layer.on('dragend', () => {
                    this.updateSinglePoiPolygonFromLayer(layer)
                })

                layer._poiPolygonDragBound = true
            },
            polygonLatLngsFromGeoJson(geojson) {
                if (!geojson || !geojson.geometry || geojson.geometry.type !== 'Polygon') return []

                return (geojson.geometry.coordinates[0] || []).map((coordinate) => [
                    coordinate[1],
                    coordinate[0]
                ])
            },
            movePoiCoordinates(latLng) {
                this.singlePoi.lat = latLng[0]
                this.singlePoi.lng = latLng[1]
            },
            handleFocusPoi(poi) {
                const poiIndex = this.localPoisComputed.findIndex((item) => item.id === poi.id)
                if (poiIndex !== -1) this.$refs.poiScroller?.scrollToItem(poiIndex)

                this.focusPoi(poi)

                if (poi.displayLatLng) {
                    this.getPoiLeafletMap()?.setView(poi.displayLatLng, 16)
                    return
                }

                if (poi.area_type === 'polygon' && poi.polygon_geojson) {
                    const layer = L.geoJSON(poi.polygon_geojson)
                    const bounds = layer.getBounds()

                    if (bounds.isValid()) {
                        this.getPoiLeafletMap()?.fitBounds(bounds, { padding: [20, 20] })
                    }
                }
            },
            sendAlertConfig(poi) {
                if (this.poisNotifications[poi.id]) {
                    this.updatePoiNotification({
                        vueInstance: this,
                        item: poi,
                        notificationSettings: this.poiSettings
                    })
                } else {
                    this.createPoiNotification({
                        vueInstance: this,
                        item: poi,
                        notificationSettings: this.poiSettings
                    })
                }
            },
            deleteAlertConfig(poi) {
                this.$bvModal
                    .msgBoxConfirm(this.__('manage_poi.messages.delete_poi_notification_confirm'), {
                        okVariant: 'success',
                        okTitle: this.__('yes'),
                        cancelTitle: this.__('no')
                    })
                    .then((response) => {
                        if (response) {
                            this.deletePoiNotification({ vueInstance: this, item: poi })
                        }
                    })
            },
            saveAlertSettings() {
                // Filter out empty email addresses before saving
                this.poiSettings.emailAddresses = this.poiSettings.emailAddresses.filter(email => email.trim() !== '')
                // Config Bus (Sprint 18, REFACTOR-030): commitChange granulare, scope
                // 'company' (era type:'company' nel vecchio saveConfig).
                this.$store.dispatch('config/commitChange', {
                    key: 'poiSettings',
                    scope: 'company',
                    value: {
                        emailAddresses: this.poiSettings.emailAddresses
                    }
                })

                //Close modal
                this.$bvModal.hide('poi-manager-settings-modal')
                this.$snotify.success(this.__('manage_poi.messages.add_emails_success'), {
                    position: 'centerTop'
                })

                if (Object.keys(this.poisNotifications).length > 0) {
                    // Prendo il primo poi della lista e lo aggiorno con le nuove impostazioni (aggiornando la mail per lui si aggiornano tutte)
                    this.updatePoiNotification({
                        vueInstance: this,
                        item: Object.values(this.poisNotifications)[0],
                        notificationSettings: this.poiSettings
                    })
                }
            },
            isAlertButtonDisabled(poi) {
                return !poi.alert?.notification_events || poi.alert.notification_events.length === 0
            },
            ...mapMutations({
                updatePoiInStore: 'updatePoi',
                focusPoi: 'focusPoi',
                removePoi: 'removePoi'
            }),
            ...mapActions({
                loadPois: 'loadPois',
                loadPoiCategories: 'loadPoiCategories',
                createPoi: 'createPoi',
                updatePoiOnServer: 'updatePoi',
                deletePoi: 'deletePoi',
                loadPoisNotifications: 'loadPoisNotifications',
                createPoiNotification: 'createPoiNotification',
                updatePoiNotification: 'updatePoiNotification',
                deletePoiNotification: 'deletePoiNotification'
            })
        },
        computed: {
            poiTypes() {
                return [
                    { value: '', text: this.__('manage_poi.types_poi.all') },
                    ...this.poiCategories.map((category) => ({
                        value: category.id,
                        text: category.description
                    }))
                ]
            },
            localPoisCircles() {
                if (this.mapZoom < 13) return []

                return this.localPoisComputed.filter(
                    (poi) =>
                        poi.area_type !== 'polygon' &&
                        Array.isArray(poi.latLng) &&
                        this.isPoiInsideMapBounds(poi)
                )
            },
            localPoisPolygons() {
                if (this.mapZoom < 13) return []

                return this.localPoisComputed.filter(
                    (poi) =>
                        poi.area_type === 'polygon' &&
                        poi.polygon_geojson &&
                        this.isPoiInsideMapBounds(poi)
                )
            },
            eligiblePoiMarkers() {
                return this.localPoisComputed.filter((poi) => Array.isArray(poi.displayLatLng))
            },
            localPoisMarkers() {
                return this.eligiblePoiMarkers.slice(0, this.renderedPoiMarkerCount)
            },
            localPoisComputed() {
                const search = (this.searchPoi || '').trim().toLowerCase()
                const type = this.poiTypesSelected

                if (!search && !type) return this.pois

                return this.pois.filter((poi) => {
                    const matchesSearch =
                        !search || (poi.description || '').toLowerCase().includes(search)
                    const matchesType = !type || poi.type == type

                    return matchesSearch && matchesType
                })
            },
            ...mapState({
                pois: (state) => state.pois.pois,
                poiCategories: (state) => state.pois.poiCategories,
                poisNotifications: (state) => state.pois.poisNotifications,
                // Sprint 19: rimosso `config` (blob user mappato ma mai usato). `poiSettings`
                // si legge come sotto-chiave NUDA dello scope company (granulare; assente →
                // undefined, present-check come nel legacy — il default schema {} non è
                // load-bearing per l'hydration).
                storedPoiSettings: (state) => state.config.company.poiSettings
            })
        },
        watch: {
            pois() {
                this.$nextTick(() => this.schedulePoiMarkerRendering())
            },
            'pois.length'() {
                if (this._poiMapReady) {
                    this.$nextTick(() => {
                        this.schedulePoiMapLayout()
                        this.scheduleInitialPoiBoundsFit()
                    })
                }
            },
            searchPoi() {
                this.$nextTick(() => this.schedulePoiMarkerRendering())
            },
            poiTypesSelected() {
                this.$nextTick(() => this.schedulePoiMarkerRendering())
            },
            mode(val) {
                if (val === 'create' || val === 'update') {
                    this.loadPoiCategories({ vueInstance: this })
                }
                if (val != 'update') this.resetSinglePoi()
                this.$nextTick(() => this.syncPolygonDrawingMode())
                if (val === 'list') {
                    this.$nextTick(() => this.schedulePoiMarkerRendering())
                }
            },
            'singlePoi.area_type': {
                handler() {
                    this.$nextTick(() => this.syncPolygonDrawingMode())
                }
            },
            'singlePoi.color': {
                handler() {
                    if (!this.drawLayerGroup) return
                    const style = this.getPolygonStyle(this.singlePoi)
                    this.drawLayerGroup.eachLayer((layer) => {
                        if (typeof layer.setStyle === 'function') layer.setStyle(style)
                    })
                    this.$nextTick(() => this.syncPolygonDrawingMode())
                }
            },
            'singlePoi.polygon_geojson': {
                handler() {
                    if (this.singlePoi.area_type === 'polygon') {
                        this.$nextTick(() => this.syncLayerFromSinglePoiPolygon())
                    }
                }
            },
            'singlePoi.lat': {
                handler(val) {
                    if (
                        val &&
                        this.singlePoi.area_type !== 'polygon' &&
                        this.$refs.map &&
                        this.$refs.map.lmap
                    ) {
                        this.$refs.map.lmap.setView([val, this.singlePoi.lng], 16)
                    }
                }
            },
            // Sprint 19: watch granulare sulla sotto-chiave (era deep-watch sull'intero blob
            // company). Scatta all'hydration del load e a ogni commitChange di poiSettings
            // (SET_VALUE riassegna la chiave); il deep non serve — l'eco delle proprie write
            // è un self-assign innocuo.
            storedPoiSettings(val) {
                if (val) {
                    this.poiSettings = val
                }
            },
            poiCategories: {
                handler(categories) {
                    if (!this.singlePoi.id && !this.singlePoi.type && categories.length) {
                        this.singlePoi.type = categories[0].id
                    }
                },
                immediate: true
            }
        },
        mounted() {
            if (L.LineUtil && !L.LineUtil._flat && typeof L.LineUtil.isFlat === 'function') {
                L.LineUtil._flat = L.LineUtil.isFlat
            }
        },
        beforeDestroy() {
            this._poiMapReady = false
            clearTimeout(this._poiMarkerBatchTimer)
            clearTimeout(this._poiBoundsFitTimer)
            const layoutTimers = this._poiMapLayoutTimers || []
            layoutTimers.forEach((timer) => clearTimeout(timer))
            this._poiMapLayoutTimers = []
            const map = this.getPoiLeafletMap()
            if (map) map.off('zoomend moveend', this.updatePoiMapViewport)
            if (map && this.drawControl) {
                map.removeControl(this.drawControl)
            }
        }
    }
</script>

<style scoped lang="scss">
    .btn-add-poi {
        margin-right: 5px;
        text-wrap: nowrap;
    }

    .activity-panel ::v-deep(.head) {
        flex-basis: 50px;
    }

    .map-section {
        padding: 8px;
        height: 100%;
        flex: 1 1 70%;
        min-width: 0;
        min-height: 0;

        display: flex;
        flex-direction: column;

        position: relative;

        .select-on-map {
            position: absolute;
            top: 8px;
            left: 50%;
            transform: translateX(-50%);
            z-index: 1000;

            ::v-deep(button) {
                background-color: transparent;
                border: 0px;
            }
        }
    }

    .geofleet-map {
        border-radius: 25px;
    }

    .pois-list-wrapper {
        display: flex;
        flex-direction: column;
        flex: 0 1 30%;
        min-width: 280px;
        min-height: 0;
        margin: 8px 12px 8px 8px;
        position: relative;

        .pois-list.custom-scrollbar {
            height: 100%;
            overflow-y: auto;
            overflow-x: hidden;
            padding-right: 5px;
        }

        .poi-list-item {
            cursor: pointer;

            &.focused-poi .golia-list-item {
                background-color: var(--main-focus);
            }

            .poi-icon {
                font-family: 'Font Awesome Kit';
                font-style: initial;
            }

            ::v-deep(.golia-list-item) {
                border-left: 5px solid var(--item-border-color, #fff);

                .item-actions {
                    margin-left: 15px;
                }

                p.item-subtitle {
                    margin-bottom: 0;
                    display: flex;

                    .icon-wrapper {
                        flex: 0 0 25px;
                    }

                    > span {
                        flex: 1;
                    }

                    &.item-address {
                        margin-top: 5px;

                        .address-text {
                            font-size: 0.75rem;
                            color: #666;
                            line-height: 1.4;
                        }
                    }
                }

                // .item-address {
                //     display: flex;
                //     padding-left: 3px;

                //     i {
                //         margin-right: 5px;
                //     }

                //     small {
                //         font-size: .75rem;
                //     }
                // }
            }

            .alert-form {
                .alert-type-input {
                    min-width: auto;
                }

                .btn-save-alert {
                    display: flex;
                    align-items: center;
                }
            }
        }
    }

    .form-container {
        border-radius: 25px;
        padding: 15px;
        background-color: var(--main-inner-bg-color);
    }

    .no-pois {
        border-radius: 25px;
    }
</style>
